/**
 * Faults and warnings on the trailer page (Eric, 2026-11-04): what each active code is, where it
 * came from, how often, and what to do, plus codes that cleared in the last 7 days so a flapping
 * code is easy to spot. Staff only: `enabled` comes from the page's isInternal, and the server
 * answers 403 to anyone else regardless.
 *
 * Colour carries meaning only: the theme coral for a critical code or a fault, the existing
 * warning colour for a warning, nothing for a notice.
 */
import { useEffect, useRef } from 'react';
import { format, formatDistanceToNowStrict } from 'date-fns';
import { cn } from '@/lib/utils';
import { useUnitFaults } from '@/hooks/useUnitFaults';
import type { UnitClearedFaultRow, UnitFaultRow } from '@/lib/unitFaultsApi';

export type UnitFaultsAnchor = 'unit-faults-faults' | 'unit-faults-warnings';

interface UnitFaultsSectionProps {
  unitId: string | null | undefined;
  /** Staff only. False renders nothing and asks the server nothing. */
  enabled: boolean;
  /** Changes when the page's fault or warning counts change, so the rows are read again. */
  refreshKey?: string | number;
  /**
   * A "See details" click. Scrolls once the rows are there, to the group asked for, or to the
   * section when that group has since cleared. `seq` makes a repeat click scroll again.
   */
  focus?: { anchor: UnitFaultsAnchor; seq: number } | null;
}

const SEVERITY_LABEL: Record<string, string> = {
  critical: 'Critical',
  fault: 'Fault',
  warning: 'Warning',
  info: 'Info',
  ignore: 'Notice',
};

type Tone = 'coral' | 'warning' | 'neutral';

/** The catalogue's rating when there is one, else the kind the device reported. */
function toneFor(row: UnitFaultRow): Tone {
  const severity = row.catalogue?.severity ?? row.severity;
  if (severity === 'critical' || severity === 'fault') return 'coral';
  if (severity === 'warning') return 'warning';
  return 'neutral';
}

const TONE_BORDER: Record<Tone, string> = {
  coral: 'border-l-[color:var(--live-coral)]',
  warning: 'border-l-warning',
  neutral: 'border-l-border',
};

const TONE_TEXT: Record<Tone, string> = {
  coral: 'text-[color:var(--live-coral)]',
  warning: 'text-warning',
  neutral: 'text-muted-foreground',
};

function when(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '—' : format(date, 'MMM d, h:mm a');
}

function ago(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '—' : `${formatDistanceToNowStrict(date)} ago`;
}

function seen(count: number): string {
  return count === 1 ? 'Seen once' : `Seen ${count} times`;
}

function title(row: UnitFaultRow): string {
  return row.catalogue?.message || row.vendor_name || row.code;
}

function ActiveRow({ row }: { row: UnitFaultRow }) {
  const tone = toneFor(row);
  const severity = row.catalogue?.severity ?? row.severity;
  return (
    <li
      data-fault-code={row.code}
      className={cn('rounded-md border border-border border-l-2 bg-card px-3 py-2.5', TONE_BORDER[tone])}
    >
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className={cn('font-mono text-xs font-semibold tabular-nums', TONE_TEXT[tone])}>{row.code}</span>
        <span className="text-sm font-medium text-foreground">{title(row)}</span>
        <span className={cn('text-xs', TONE_TEXT[tone])}>{SEVERITY_LABEL[severity] ?? severity}</span>
        {row.catalogue?.draft && <span className="text-xs text-muted-foreground">Draft wording</span>}
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        {row.system}
        {row.vendor_name ? ` · ${row.vendor_name}` : ''}
        {!row.catalogue && ' · Not in the catalogue yet'}
      </p>
      <p className="mt-0.5 text-xs text-muted-foreground tabular-nums">
        {seen(row.count)} · first {when(row.first_seen)} · last {ago(row.last_seen)}
      </p>
      {row.catalogue?.recommended_action && (
        <p className="mt-2 text-sm text-foreground">
          <span className="text-muted-foreground">What to do: </span>
          {row.catalogue.recommended_action}
        </p>
      )}
    </li>
  );
}

function ClearedRow({ row }: { row: UnitClearedFaultRow }) {
  return (
    <li data-fault-code={row.code} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 py-1.5">
      <span className="font-mono text-xs text-muted-foreground tabular-nums">{row.code}</span>
      <span className="text-sm text-foreground">{title(row)}</span>
      {row.catalogue?.draft && <span className="text-xs text-muted-foreground">Draft wording</span>}
      {!row.catalogue && <span className="text-xs text-muted-foreground">Not in the catalogue yet</span>}
      <span className="w-full text-xs text-muted-foreground tabular-nums sm:w-auto sm:ml-auto">
        Cleared {ago(row.cleared_at)} · {row.cleared_by === 'acknowledged' ? 'acknowledged' : 'aged out'} · {seen(row.count).toLowerCase()}
      </span>
    </li>
  );
}

function Group({ id, label, rows }: { id: string; label: string; rows: UnitFaultRow[] }) {
  if (rows.length === 0) return null;
  // The banner counts events ("12 Active Warnings"); say both so the two never seem to disagree.
  const events = rows.reduce((sum, row) => sum + row.count, 0);
  return (
    <div id={id} className="scroll-mt-24 space-y-2">
      <h3 className="text-xs text-muted-foreground">
        <span className="font-medium uppercase tracking-wide">{label}</span>
        <span className="ml-2 tabular-nums">
          {rows.length} {rows.length === 1 ? 'code' : 'codes'} · {events} {events === 1 ? 'event' : 'events'}
        </span>
      </h3>
      <ul className="space-y-2">
        {rows.map((row) => <ActiveRow key={`${row.source}:${row.severity}:${row.code}`} row={row} />)}
      </ul>
    </div>
  );
}

export function UnitFaultsSection({ unitId, enabled, refreshKey, focus }: UnitFaultsSectionProps) {
  const { data, error } = useUnitFaults(unitId, enabled, refreshKey);
  // The last click handled: each click scrolls once, and a later refresh of the rows leaves the
  // page where the reader put it.
  const handledSeq = useRef(0);

  useEffect(() => {
    if (!focus || !data || focus.seq <= handledSeq.current) return;
    handledSeq.current = focus.seq;
    const target = document.getElementById(focus.anchor) ?? document.getElementById('unit-faults');
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [focus, data]);

  if (!enabled) return null;

  if (error) {
    return (
      <section id="unit-faults" className="rounded-lg border border-border p-4 text-sm text-muted-foreground">
        Faults and warnings couldn't load. Refresh to try again.
      </section>
    );
  }
  if (!data || (data.active.length === 0 && data.recently_cleared.length === 0)) return null;

  const faults = data.active.filter((row) => row.severity === 'fault');
  const warnings = data.active.filter((row) => row.severity !== 'fault');

  return (
    <section id="unit-faults" aria-labelledby="unit-faults-title" className="rounded-lg border border-border p-4 space-y-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="unit-faults-title" className="text-base font-semibold tracking-tight">Faults and warnings</h2>
        <span className="text-xs text-muted-foreground">Last {data.window_days} days · staff only</span>
      </div>

      {data.active.length === 0 && (
        <p className="text-sm text-muted-foreground">Nothing active right now.</p>
      )}
      <Group id="unit-faults-faults" label="Faults" rows={faults} />
      <Group id="unit-faults-warnings" label="Warnings" rows={warnings} />

      {data.recently_cleared.length > 0 && (
        <div className="space-y-1 border-t border-border pt-3">
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Recently cleared <span className="tabular-nums">({data.recently_cleared.length})</span>
          </h3>
          <ul className="divide-y divide-border">
            {data.recently_cleared.map((row) => (
              <ClearedRow key={`${row.source}:${row.severity}:${row.code}`} row={row} />
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
